import React, { useCallback, useEffect, useState } from 'react';
import { useFieldArray } from 'react-hook-form';
import {
  ATMButton,
  ATMField,
  ATMForm,
  ATMGrid,
  ATMRadio,
  ATMTable,
  ATMInput,
} from 'shared-it-appmod-ui';
import Confirm from 'src/components/atoms/confirm/confirm.component';
import LabelRequired from 'src/components/atoms/label-required/label-required.component';
import Lang from 'src/libraries/language';
import { IRequestFormMethods } from 'src/models/request.model';
import { SettingKey } from 'src/constants';
import { useSetting } from 'src/hooks/setting.hook';
import { ToastSuccess } from 'src/components/atoms/toaster/toaster.component';

type IProps = IRequestFormMethods;

const FlightRequestPatrolPassengerEquipment: React.FC<IProps> = ({
  control,
  errors,
}) => {
  const [flag, setFlag] = useState<boolean | undefined>(
    control.defaultValuesRef?.current?.passengerFlight?.extraEquipmentFlag
  );
  const limit = useSetting(SettingKey.MaxEquipments, 1);
  const [deleteEquipment, setDeleteEquipment] = useState<string>();
  const [equipments, setEquipments] = useState<Record<string, string>>({});

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'passengerFlight.equipmentInfos',
  });

  const handleAdd = useCallback(() => {
    append({
      type: '',
      weight: '',
    });
  }, [append]);

  useEffect(() => {
    if (!fields.length) {
      handleAdd();
    }
  }, [fields, handleAdd]);

  useEffect(() => {
    fields.forEach((value) => {
      if (value.id) {
        setEquipments((values) => {
          return {
            ...values,
            [value.id]: value.type,
          };
        });
      }
    });
  }, [fields]);

  const handleFlag = useCallback(
    (value) => {
      const isChecked = value === 'yes';
      control.setValue('passengerFlight.extraEquipmentFlag', isChecked, {
        shouldValidate: true,
      });
      setFlag(isChecked);
    },
    [control, setFlag]
  );

  return (
    <ATMGrid columns={1}>
      <ATMGrid.Column>
        <ATMForm.Field
          error={!!errors?.passengerFlight?.extraEquipmentFlag}
          inline
        >
          <LabelRequired>{Lang.LBL_EXTRA_EQUIPMENT}</LabelRequired>

          <div className="inline-block">
            <ATMField
              className="hidden"
              as={ATMInput}
              control={control}
              type="hidden"
              name="passengerFlight.extraEquipmentFlag"
            />
            <ATMForm.Group>
              <ATMForm.Field>
                <ATMRadio
                  label={Lang.LBL_YES}
                  value="yes"
                  checked={flag === true}
                  onChange={(_, { value }) => handleFlag(value)}
                />
              </ATMForm.Field>
              <ATMForm.Field>
                <ATMRadio
                  label={Lang.LBL_NO}
                  value="no"
                  checked={flag === false}
                  onChange={(_, { value }) => handleFlag(value)}
                />
              </ATMForm.Field>
            </ATMForm.Group>
          </div>
        </ATMForm.Field>
      </ATMGrid.Column>

      {flag && (
        <ATMGrid.Column>
          <ATMTable>
            <ATMTable.Header>
              <ATMTable.Row>
                <ATMTable.HeaderCell width={8}>
                  {Lang.LBL_EQUIPMENT_TYPE}
                </ATMTable.HeaderCell>
                <ATMTable.HeaderCell width={7}>
                  {Lang.LBL_EQUIPMENT_WEIGHT}
                </ATMTable.HeaderCell>
                <ATMTable.HeaderCell width={1} />
              </ATMTable.Row>
            </ATMTable.Header>

            <ATMTable.Body>
              {fields.map((item, i) => (
                <ATMTable.Row key={item.id}>
                  <ATMTable.Cell>
                    <ATMField
                      name={`passengerFlight.equipmentInfos[${i}].type`}
                      as={ATMInput}
                      control={control}
                      placeholder={Lang.LBL_ENTER}
                      onChange={([_, { value }]) => {
                        setEquipments((values) => {
                          return {
                            ...values,
                            [item.id]: value.type,
                          };
                        });

                        return value;
                      }}
                      error={
                        Array.isArray(errors.passengerFlight?.equipmentInfos)
                          ? errors.passengerFlight?.equipmentInfos[i]?.type
                          : false
                      }
                    />
                  </ATMTable.Cell>
                  <ATMTable.Cell>
                    <ATMField
                      name={`passengerFlight.equipmentInfos[${i}].weight`}
                      as={ATMInput}
                      control={control}
                      placeholder={Lang.LBL_ENTER}
                      error={
                        Array.isArray(errors.passengerFlight?.equipmentInfos)
                          ? errors.passengerFlight?.equipmentInfos[i]?.weight
                          : false
                      }
                    />
                  </ATMTable.Cell>

                  <ATMTable.Cell width={2} textAlign="right">
                    {fields.length > 1 && (
                      <ATMButton
                        icon="trash alternate outline"
                        type="button"
                        onClick={() =>
                          item.type ? setDeleteEquipment(item.id) : remove(i)
                        }
                      />
                    )}
                  </ATMTable.Cell>
                </ATMTable.Row>
              ))}
            </ATMTable.Body>
            <ATMTable.Footer>
              <ATMTable.Row>
                <ATMTable.HeaderCell colSpan={4}>
                  <ATMButton
                    secondary
                    icon="plus"
                    type="button"
                    onClick={handleAdd}
                    disabled={fields.length >= limit}
                    content={Lang.LBL_ADD}
                  />
                </ATMTable.HeaderCell>
              </ATMTable.Row>
            </ATMTable.Footer>
          </ATMTable>
        </ATMGrid.Column>
      )}
      <Confirm
        open={deleteEquipment !== undefined}
        size="tiny"
        onCancel={() => setDeleteEquipment(undefined)}
        header={Lang.TTL_CONFIRM_DELETE}
        content={Lang.formatString(
          Lang.MSG_CONFIRM_DELETE,
          equipments[deleteEquipment ?? 0] ?? ''
        )}
        onConfirm={() => {
          remove(fields.findIndex((value) => value.id === deleteEquipment));
          ToastSuccess(
            Lang.formatString(
              Lang.MSG_EQUIPMENT_DELETE_SUCCESS,
              equipments[deleteEquipment ?? 0] ?? ''
            )
          );
          setDeleteEquipment(undefined);
        }}
        confirmText={Lang.LBL_YES_DELETE}
      />
    </ATMGrid>
  );
};

export default FlightRequestPatrolPassengerEquipment;
